<template>
  <!-- Case > Livestorm vs Competitor -->
  <TemplateComparisonsLivestormVsCompetitor
    v-if="isLivestormSoftware"
    v-bind="data"
  />
  <!-- Case > Competitor vs Competitor -->
  <TemplateComparisonsCompetitorVsCompetitor
    v-else
    v-bind="data"
  />
</template>

<script setup>
// Layout
definePageMeta({
  layout: 'default',
  middleware: [
    'check-if-the-software-comparison-exists',
  ],
})

// i18n
defineI18nRoute({
  locales: ['en', 'es', 'fr'],
  paths: {
    en: '/webinar-software-comparison/[slug]',
    es: '/comparacion-software-seminarios-web/[slug]',
    fr: '/comparatif-logiciels-webinar/[slug]',
  },
})

// Composables
const { setAvailableLanguages } = useAvailableLanguages()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const localePath = useLocalePath()
const { $interpolate } = useNuxtApp()
const { meta, params } = useRoute()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()

// Async Data
const { data } = await useFetch(`/api/content/pages/software-comparisons/${locale.value}`, {
  key: `page-webinar-software-comparison-[slug]-${params.slug}-${locale.value}`,
  query: {
    preview: state.preview,
  },
  transform: (input) => {
    const { allModelComparisons, page: hub, ...props } = input
    const allPages = allModelComparisons.filter(comparison =>
      comparison.company1.companyName === 'Livestorm'
        ? comparison.livestormTextTab.length > 0
        : comparison.aVsBTextTab.length > 0,
    )
    const page = allPages
      .filter(comparison =>
        comparison.company1.companyName === 'Livestorm'
          ? comparison.livestormTextTab.length > 0
          : comparison.aVsBTextTab.length > 0,
      )
      .find(({ slug }) => slug === params.slug)
    return {
      ...props,
      hub,
      page,
    }
  },
})

if (!data.value?.page) {
  throw createError({ statusCode: 404, message: 'Page not found' })
}

// Computed Properties
const isLivestormSoftware = computed(() => {
  return data.value.page.company1.companyName === 'Livestorm'
})

const seoTitle = computed(() => {
  return isLivestormSoftware.value
    ? $interpolate(data.value.componentContentLivestorm.seoTitle, {
        '%{company2}': data.value.page.company2.companyName,
      })
    : $interpolate(data.value.componentContentAVsB.seoTitle, {
        '%{company1}': data.value.page.company1.companyName,
        '%{company2}': data.value.page.company2.companyName,
      })
})

const seoDescription = computed(() => {
  return isLivestormSoftware.value
    ? $interpolate(data.value.componentContentLivestorm.seoDescription, {
        '%{company2}': data.value.page.company2.companyName,
      })
    : $interpolate(data.value.componentContentAVsB.seoDescription, {
        '%{company1}': data.value.page.company1.companyName,
        '%{company2}': data.value.page.company2.companyName,
      })
})

// Meta
const alternativeVersions = isLivestormSoftware.value
  ? data.value.page._allLivestormTextTabLocales
  : data.value.page._allAVsBTextTabLocales
const locales = alternativeVersions.map(({ locale }) => locale)

meta.nuxtI18n = alternativeVersions.reduce(
  (accumator, { locale, value: slug }) => ({
    ...accumator,
    [locale]: { slug },
  }),
  {},
)

const head = useLocaleHead({
  identifierAttribute: 'id',
  addSeoAttributes: true,
})

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  ogTitle: seoTitle,
  ogDescription: seoDescription,
  twitterTitle: seoTitle,
  twitterDescription: seoDescription,
})

useHead({
  link: head.value.link
    .filter((link) => {
      return link.rel === 'canonical'
        || (link.rel === 'alternate' && link.hreflang === 'x-default')
        || (link.rel === 'alternate' && locales.includes(link.hreflang))
    })
    .map((link) => {
      return {
        ...link,
        href: !locales.includes('en') && link.id === 'i18n-xd'
          ? `${publicRuntimeConfig.baseUrl}${localePath({ name: getRouteBaseName() })}`
          : link.href,
      }
    }),
  meta: head.value.meta.filter((meta) => {
    return meta.property === 'og:url'
      || meta.property === 'og:locale'
      || (meta.property === 'og:locale:alternate' && locales.includes(meta.content))
  }),
  script: [
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.hub.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('software-comparison')}`,
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': `${data.value.page.company1.companyName} vs ${data.value.page.company2.companyName}`,
          },
        ],
      }),
      type: 'application/ld+json',
    },
    isLivestormSoftware.value
      ? {}
      : {
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            'mainEntity': [
              {
                '@type': 'Question',
                'name': $interpolate(
                  data.value.componentContentAVsB.whatIsTitle1,
                  {
                    '%{company1}': data.value.page.company1.companyName,
                  },
                ),
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': data.value.page.whatIsText1,
                },
              },
              {
                '@type': 'Question',
                'name': $interpolate(
                  data.value.componentContentAVsB.whatIsTitle2,
                  {
                    '%{company2}': data.value.page.company2.companyName,
                  },
                ),
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': data.value.page.whatIsText2,
                },
              },
            ],
          }),
          type: 'application/ld+json',
        },
  ],
})

onMounted(() => {
  setAvailableLanguages(locales)
})
</script>
